import { c as _c } from "react/compiler-runtime";
import React, { useContext, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { BarChart3, BookOpen, LifeBuoy, LogOut, Monitor, Moon, Palette, SettingsIcon, Sun, Wrench } from "lucide-react";
import { useTheme } from "next-themes";
import posthog from "posthog-js";
import { AuthContext } from "../AuthProvider";
import { NotificationFloatingButton } from "../NotificationFloatingButton";
import { StatusWidgetMorph, RailStatusHoverCard } from "./StatusIndicator";
import { BSErrorBoundary } from "../ui/bs-error-boundary";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Button } from "@/components/ui/button";
import { useSetPersistedTheme } from "@/hooks/useAppearanceSettings";
import { useIsHydrated } from "@/components/codesmith/useIsHydrated";
import { cn } from "@/lib/utils";
export type SidebarActiveSection = "actions" | "codesmith" | "usage" | "admin";
export const RAIL_ROW = cn("flex h-8 w-full items-center overflow-hidden rounded-md cursor-pointer transition-none", "type-body-s-regular text-neutral-800 hover:bg-black/[0.04] hover:text-neutral-950", "[&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900", "dark:text-neutral-300 dark:hover:bg-white/[0.04] dark:hover:text-white dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]");
export const RAIL_ROW_ACTIVE = "bg-[#0000000f] text-neutral-950 [&_svg]:text-neutral-900 dark:bg-white/6 dark:text-white dark:[&_svg]:text-white";
export function RailRowContents(t0) {
  const $ = _c(15);
  const {
    icon,
    label,
    badge,
    expanded: t1,
    hideLabelWhenCollapsed: t2
  } = t0;
  const expanded = t1 === undefined ? true : t1;
  const hideLabelWhenCollapsed = t2 === undefined ? true : t2;
  const collapsedAndRemoved = hideLabelWhenCollapsed && !expanded;
  let t3;
  if ($[0] !== icon) {
    t3 = <span className="flex size-8 shrink-0 items-center justify-center">{icon}</span>;
    $[0] = icon;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = collapsedAndRemoved && "hidden";
  const t5 = !badge && "pr-2";
  let t6;
  if ($[2] !== t4 || $[3] !== t5) {
    t6 = cn("whitespace-nowrap text-left", t4, t5);
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== label || $[6] !== t6) {
    t7 = <span className={t6}>{label}</span>;
    $[5] = label;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== badge || $[9] !== collapsedAndRemoved) {
    t8 = badge && !collapsedAndRemoved ? <span className="ml-auto mr-2 shrink-0">{badge}</span> : null;
    $[8] = badge;
    $[9] = collapsedAndRemoved;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t3 || $[12] !== t7 || $[13] !== t8) {
    t9 = <>{t3}{t7}{t8}</>;
    $[11] = t3;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}
const THEME_OPTIONS = [{
  value: "system",
  label: "System",
  icon: Monitor
}, {
  value: "light",
  label: "Light",
  icon: Sun
}, {
  value: "dark",
  label: "Dark",
  icon: Moon
}] as const;
export function AppearanceThemeToggle() {
  const $ = _c(5);
  const {
    theme
  } = useTheme();
  const setTheme = useSetPersistedTheme();
  const mounted = useIsHydrated();
  const activeTheme = mounted ? theme ?? "system" : "system";
  let t0;
  if ($[0] !== activeTheme || $[1] !== setTheme) {
    t0 = THEME_OPTIONS.map(t1 => {
      const {
        value,
        label,
        icon: Icon
      } = t1;
      const isActive = activeTheme === value;
      return <button key={value} type="button" aria-label={label} aria-pressed={isActive} onClick={() => setTheme(value)} className={cn("flex size-7 items-center justify-center rounded-md transition-colors", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-border)]", isActive ? "bg-[#0000000f] text-neutral-950 dark:bg-white/6 dark:text-white" : "text-neutral-600 hover:bg-black/[0.04] hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-white/[0.04] dark:hover:text-white")}><Icon className="size-3.5" /></button>;
    });
    $[0] = activeTheme;
    $[1] = setTheme;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <div className="-my-1 -mr-1 flex items-center gap-0.5">{t0}</div>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}

// A right-anchored label tooltip for a collapsed rail/sidebar row. Used both by
// the icon-collapsible sidebar and by the slim app-switcher rail (which no
// longer hover-expands to reveal labels, so each row surfaces its label here).
export function CollapsedIconTooltip(t0) {
  const $ = _c(7);
  const {
    label,
    enabled,
    children
  } = t0;
  if (!enabled) {
    return children;
  }
  let t1;
  if ($[0] !== children) {
    t1 = <TooltipTrigger asChild={true}>{children}</TooltipTrigger>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <TooltipContent side="right">{label}</TooltipContent>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Tooltip>{t1}{t2}</Tooltip>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function useSidebarLogout() {
  const $ = _c(2);
  const {
    logout
  } = useContext(AuthContext);
  let t0;
  if ($[0] !== logout) {
    t0 = () => {
      posthog?.reset();
      logout();
    };
    $[0] = logout;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function SidebarAdminRow(t0) {
  const $ = _c(15);
  const {
    expanded,
    active,
    onClick,
    hideLabelWhenCollapsed: t1
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const {
    user
  } = useContext(AuthContext);
  if (!user?.is_superuser) {
    return null;
  }
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="my-2"><hr className="border-[#0c0c0d14] dark:border-white/12" /></div>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = hideLabelWhenCollapsed && !expanded;
  const t4 = active ? "page" : undefined;
  const t5 = active && RAIL_ROW_ACTIVE;
  let t6;
  if ($[1] !== t5) {
    t6 = cn(RAIL_ROW, t5, "group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!min-w-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:justify-center");
    $[1] = t5;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Wrench className="size-[18px]" />;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== expanded || $[5] !== hideLabelWhenCollapsed) {
    t8 = <RailRowContents icon={t7} label="Admin" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[4] = expanded;
    $[5] = hideLabelWhenCollapsed;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== onClick || $[8] !== t4 || $[9] !== t6 || $[10] !== t8) {
    t9 = <Link href="/admin" onClick={onClick} aria-label="Admin" aria-current={t4} className={t6}>{t8}</Link>;
    $[7] = onClick;
    $[8] = t4;
    $[9] = t6;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t3 || $[13] !== t9) {
    t10 = <>{t2}<CollapsedIconTooltip label="Admin" enabled={t3}>{t9}</CollapsedIconTooltip></>;
    $[12] = t3;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
}
export function SidebarGlobalFooter(t0) {
  const $ = _c(74);
  const {
    expanded,
    currentOrg,
    settingsHref,
    activeSection,
    onSectionClick,
    className,
    hideLabelWhenCollapsed: t1,
    statusAsHoverCard: t2
  } = t0;
  const hideLabelWhenCollapsed = t1 === undefined ? true : t1;
  const statusAsHoverCard = t2 === undefined ? false : t2;
  const {
    user
  } = useContext(AuthContext);
  const handleLogout = useSidebarLogout();
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsTriggerRef = useRef(null);
  const settingsMenuCloseTimer = useRef(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (settingsMenuCloseTimer.current) {
        clearTimeout(settingsMenuCloseTimer.current);
        settingsMenuCloseTimer.current = null;
      }
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const cancelSettingsMenuClose = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      cancelSettingsMenuClose();
      setSettingsMenuOpen(true);
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const openSettingsMenu = t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      cancelSettingsMenuClose();
      settingsMenuCloseTimer.current = setTimeout(() => setSettingsMenuOpen(false), 120);
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const scheduleSettingsMenuClose = t5;
  let t6;
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => cancelSettingsMenuClose;
    t7 = [];
    $[3] = t6;
    $[4] = t7;
  } else {
    t6 = $[3];
    t7 = $[4];
  }
  useEffect(t6, t7);
  let t8;
  if ($[5] !== className) {
    t8 = cn("flex flex-col gap-1", className);
    $[5] = className;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const t9 = hideLabelWhenCollapsed && !expanded;
  const t10 = currentOrg ? `/${currentOrg}/usage` : "/usage";
  let t11;
  if ($[7] !== onSectionClick) {
    t11 = onSectionClick?.("usage");
    $[7] = onSectionClick;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const t12 = activeSection === "usage" ? "page" : undefined;
  const t13 = activeSection === "usage" && RAIL_ROW_ACTIVE;
  let t14;
  if ($[9] !== t13) {
    t14 = cn(RAIL_ROW, t13);
    $[9] = t13;
    $[10] = t14;
  } else {
    t14 = $[10];
  }
  let t15;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <BarChart3 className="size-[18px]" />;
    $[11] = t15;
  } else {
    t15 = $[11];
  }
  let t16;
  if ($[12] !== expanded || $[13] !== hideLabelWhenCollapsed) {
    t16 = <RailRowContents icon={t15} label="Usage and Billing" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[12] = expanded;
    $[13] = hideLabelWhenCollapsed;
    $[14] = t16;
  } else {
    t16 = $[14];
  }
  let t17;
  if ($[15] !== t10 || $[16] !== t11 || $[17] !== t12 || $[18] !== t14 || $[19] !== t16) {
    t17 = <Link href={t10} onClick={t11} aria-label="Usage and Billing" aria-current={t12} className={t14}>{t16}</Link>;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
    $[18] = t14;
    $[19] = t16;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== t17 || $[22] !== t9) {
    t18 = <CollapsedIconTooltip label="Usage and Billing" enabled={t9}>{t17}</CollapsedIconTooltip>;
    $[21] = t17;
    $[22] = t9;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== settingsMenuOpen) {
    t19 = event => {
      if (settingsMenuOpen && event.button === 0 && !event.ctrlKey) {
        event.preventDefault();
      }
    };
    $[24] = settingsMenuOpen;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <SettingsIcon className="size-[18px]" />;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== expanded || $[28] !== hideLabelWhenCollapsed) {
    t21 = <RailRowContents icon={t20} label="Settings" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[27] = expanded;
    $[28] = hideLabelWhenCollapsed;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== t19 || $[31] !== t21) {
    t22 = <DropdownMenuTrigger asChild={true}><button ref={settingsTriggerRef} type="button" aria-label="Settings menu" className={RAIL_ROW} onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onPointerDown={t19}>{t21}</button></DropdownMenuTrigger>;
    $[30] = t19;
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = event_1 => {
      const target = event_1.target;
      if (target instanceof Node && settingsTriggerRef.current?.contains(target)) {
        event_1.preventDefault();
      }
    };
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  let t25;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <SettingsIcon className="size-[18px] shrink-0" />;
    t25 = <span>Settings</span>;
    $[34] = t24;
    $[35] = t25;
  } else {
    t24 = $[34];
    t25 = $[35];
  }
  let t26;
  if ($[36] !== settingsHref) {
    t26 = <DropdownMenuItem asChild={true} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white"><Link href={settingsHref}>{t24}{t25}</Link></DropdownMenuItem>;
    $[36] = settingsHref;
    $[37] = t26;
  } else {
    t26 = $[37];
  }
  const t27 = currentOrg ? `/${currentOrg}/support` : "/support";
  let t28;
  let t29;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <LifeBuoy className="size-[18px] shrink-0" />;
    t29 = <span>Contact Support</span>;
    $[38] = t28;
    $[39] = t29;
  } else {
    t28 = $[38];
    t29 = $[39];
  }
  let t30;
  if ($[40] !== t27) {
    t30 = <DropdownMenuItem asChild={true} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white"><Link href={t27}>{t28}{t29}</Link></DropdownMenuItem>;
    $[40] = t27;
    $[41] = t30;
  } else {
    t30 = $[41];
  }
  let t31;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <DropdownMenuItem asChild={true} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white"><a href="https://docs.blacksmith.sh" target="_blank" rel="noopener noreferrer"><BookOpen className="size-[18px] shrink-0" /><span>Documentation...</span></a></DropdownMenuItem>;
    $[42] = t31;
  } else {
    t31 = $[42];
  }
  let t32;
  let t33;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <DropdownMenuItem onSelect={_temp2} className="flex cursor-default items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent dark:hover:bg-transparent dark:focus:bg-transparent dark:data-[highlighted]:bg-transparent"><span className="flex items-center gap-2"><Palette className="size-[18px] shrink-0 text-neutral-600 dark:text-neutral-400" /><span>Appearance</span></span><AppearanceThemeToggle /></DropdownMenuItem>;
    t33 = <DropdownMenuSeparator />;
    $[43] = t32;
    $[44] = t33;
  } else {
    t32 = $[43];
    t33 = $[44];
  }
  let t34;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <LogOut className="size-[18px] shrink-0" />;
    $[45] = t34;
  } else {
    t34 = $[45];
  }
  let t35;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <div>Log out</div>;
    $[46] = t35;
  } else {
    t35 = $[46];
  }
  let t36;
  if ($[47] !== user) {
    t36 = user?.username ? <div className="text-[12px] leading-[16px] text-neutral-800 dark:text-white/50">{user.username}</div> : null;
    $[47] = user;
    $[48] = t36;
  } else {
    t36 = $[48];
  }
  let t37;
  if ($[49] !== t36) {
    t37 = <div className="leading-tight">{t35}{t36}</div>;
    $[49] = t36;
    $[50] = t37;
  } else {
    t37 = $[50];
  }
  let t38;
  if ($[51] !== handleLogout || $[52] !== t37) {
    t38 = <DropdownMenuItem className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 [&_svg]:text-neutral-600 hover:[&_svg]:text-neutral-900 focus:[&_svg]:text-neutral-900 dark:[&_svg]:text-neutral-400 dark:hover:[&_svg]:text-white dark:focus:[&_svg]:text-white" onClick={handleLogout}>{t34}{t37}</DropdownMenuItem>;
    $[51] = handleLogout;
    $[52] = t37;
    $[53] = t38;
  } else {
    t38 = $[53];
  }
  let t39;
  if ($[54] !== t26 || $[55] !== t30 || $[56] !== t38) {
    t39 = <DropdownMenuContent side="right" align="start" sideOffset={8} alignOffset={-36} className="w-[232px]" onPointerEnter={openSettingsMenu} onPointerLeave={scheduleSettingsMenuClose} onCloseAutoFocus={_temp} onPointerDownOutside={t23}>{t26}{t30}{t31}{t32}{t33}{t38}</DropdownMenuContent>;
    $[54] = t26;
    $[55] = t30;
    $[56] = t38;
    $[57] = t39;
  } else {
    t39 = $[57];
  }
  let t40;
  if ($[58] !== settingsMenuOpen || $[59] !== t22 || $[60] !== t39) {
    t40 = <DropdownMenu modal={false} open={settingsMenuOpen} onOpenChange={setSettingsMenuOpen}>{t22}{t39}</DropdownMenu>;
    $[58] = settingsMenuOpen;
    $[59] = t22;
    $[60] = t39;
    $[61] = t40;
  } else {
    t40 = $[61];
  }
  let t41;
  if ($[62] !== expanded || $[63] !== hideLabelWhenCollapsed) {
    t41 = <NotificationFloatingButton variant="sidebar" label="Notifications" expanded={expanded} hideLabelWhenCollapsed={hideLabelWhenCollapsed} />;
    $[62] = expanded;
    $[63] = hideLabelWhenCollapsed;
    $[64] = t41;
  } else {
    t41 = $[64];
  }
  let t42;
  if ($[65] !== expanded || $[66] !== statusAsHoverCard) {
    t42 = <BSErrorBoundary title="Status Indicator" fallback={null}><div className="pt-1">{statusAsHoverCard ? <RailStatusHoverCard /> : <StatusWidgetMorph expanded={expanded} />}</div></BSErrorBoundary>;
    $[65] = expanded;
    $[66] = statusAsHoverCard;
    $[67] = t42;
  } else {
    t42 = $[67];
  }
  let t43;
  if ($[68] !== t18 || $[69] !== t40 || $[70] !== t41 || $[71] !== t42 || $[72] !== t8) {
    t43 = <div className={t8}>{t18}{t40}{t41}{t42}</div>;
    $[68] = t18;
    $[69] = t40;
    $[70] = t41;
    $[71] = t42;
    $[72] = t8;
    $[73] = t43;
  } else {
    t43 = $[73];
  }
  return t43;
}
function _temp2(event_2) {
  return event_2.preventDefault();
}
function _temp(event_0) {
  return event_0.preventDefault();
}